import { memo } from "react";
import { DollarSign, MapPin, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatCurrency } from "@/shared/lib/formatters";
import type { RentalSummaryResponseDTO } from "../types/rental.types";

interface RentalSummaryCardsProps {
  summary: RentalSummaryResponseDTO;
  loading: boolean;
  /**
   * Rótulo do card de receita — nos modos de busca por ID/contrato o
   * período é omitido da request e "Receita do período" mentiria.
   */
  revenueLabel?: string;
}

interface CardConfig {
  key: keyof RentalSummaryResponseDTO;
  label: string;
  icon: typeof DollarSign;
  format: (value: number) => string;
}

const CARDS: CardConfig[] = [
  {
    key: "totalRevenue",
    label: "Receita do período",
    icon: DollarSign,
    format: formatCurrency,
  },
  {
    key: "onStreetCount",
    label: "Aluguéis em rua",
    icon: MapPin,
    format: (value) => String(value),
  },
  {
    key: "incompleteCount",
    label: "Aluguéis incompletos",
    icon: AlertCircle,
    format: (value) => String(value),
  },
];

export const RentalSummaryCards = memo(function RentalSummaryCards({
  summary,
  loading,
  revenueLabel,
}: RentalSummaryCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const label =
          card.key === "totalRevenue" && revenueLabel
            ? revenueLabel
            : card.label;
        return (
          <Card key={card.key}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {label}
              </CardTitle>
              <Icon
                className="w-4 h-4 text-muted-foreground"
                aria-hidden="true"
              />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-8 flex items-center">
                  <Spinner className="w-4 h-4" />
                </div>
              ) : (
                <p className="text-2xl font-bold">
                  {card.format(summary[card.key])}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
});
